import React, { ChangeEvent, useState } from 'react';
import ErrorIcon from "../../../../assets/icons/error.svg";
import DropdownIcon from '../../../../assets/icons/dropdown.svg';
import './styles/index.scss';

interface FormInputProps {
  id: string;
  label: string;
  type: string;
  placeholder?: string;
  value: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  maxLength?: number;
  hint?: string;
  unitsSelector?: React.ReactNode,
}

interface FormSelectProps {
  id: string;
  label?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: () => void;
  error?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  required?: boolean;
  hint?: string;
}

interface FormSectionProps {
  title?: string;
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

interface DropdownProps {
  title: string | React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const FormInput: React.FC<FormInputProps> = ({
  id,
  label,
  type,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  required,
  maxLength,
  unitsSelector,
  hint
}) => (
  <div className={`form-field ${error ? "error" : ""}`}>
    <label htmlFor={id}>
      {label} {required && <span className="required">*</span>}
    </label>
    {hint && <p className="hint">{hint}</p>}
    <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        maxLength={maxLength}
        style={{ flex: '3' }}
      />
      { unitsSelector && <div style={{ flex: '1' }}>{unitsSelector}</div>}
    </div>
    {error && (
      <div className="error">
        <img src={ErrorIcon} alt="Error" />
        {error}
      </div>
    )}
  </div>
);

export const FormSelect: React.FC<FormSelectProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  options,
  required,
  hint
}) => (
  <div className={`form-field ${error ? "error" : ""}`}>
    {
      label && <label htmlFor={id}>
      {label} {required && <span className="required">*</span>}
    </label>
    }
    {hint && <p className="hint">{hint}</p>}
    <select
      id={id}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
    >
      {options.map(option => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <div className="error">
        <img src={ErrorIcon} alt="Error" />
        {error}
      </div>
    )}
  </div>
);

export const FormDropdown: React.FC<DropdownProps> = ({
  title,
  expanded: controlledExpanded,
  onToggle: controlledOnToggle,
  icon,
  children,
  className = ''
}) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  
  const isControlled = controlledExpanded !== undefined && controlledOnToggle !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  
  const handleToggle = () => {
    if (isControlled) {
      controlledOnToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <div className={`form-dropdown__container ${className}`}>
      <button
        type="button"
        className="form-dropdown__button"
        onClick={handleToggle}
      >
        <div className="form-dropdown__button-elements">
          {icon && <div className="form-dropdown__icon">{icon}</div>}
          <span>{title}</span>
        </div>
        <div className={`dropdown__icon ${expanded ? 'dropdown__icon--expanded' : ''}`}>
          <img src={DropdownIcon} alt="Toggle" />
        </div>
      </button>
      <div className={`form-dropdown__content${expanded ? ' expanded' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  expanded = true,
  onToggle,
  children,
  icon
}) => (
  <div className="form-section">
    {title ? (
      <FormDropdown
        title={title}
        expanded={expanded}
        onToggle={onToggle!}
        icon={icon}
      >
        {children}
      </FormDropdown>
    ) : (
      <div className="form-section__content expanded">
        {children}
      </div>
    )}
  </div>
); 